import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { set } from "lodash";

// MUI
import { Grid } from "@mui/material";

// Components
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import ModelBased from "@PayPlans/components/Defaults/Sales/ModelBasedCommissions/ModelBased";
import SectionContainer from "@/Layouts/components/SectionContainer";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import { useErrors } from "@/Common/hooks/Errors";

// Constants
const AREAS = {
	new: {
		key: "new",
		label: "New Vehicles",
	},
	preOwned: {
		key: "preOwned",
		label: "Pre-Owned Vehicles",
	},
};

const getTabAreaKey = (index = 0) => {
	const [, value] = Object.entries(AREAS)?.[index] || [];
	return value?.key;
};

const mapInitialModel = (content) => {
	const model = { ...content };
	Object.keys(AREAS).forEach((key) => {
		const definitions = model?.[key]?.definitions || [];
		const totalSpecialUnits = Boolean(model?.[key]?.totalSpecialUnits);
		set(model, [key, "definitions"], definitions);
		set(model, [key, "totalSpecialUnits"], totalSpecialUnits);
	});
	return model;
};

const isAllValid = (errors = []) => errors.every((error) => !error);

const getTotalSpecialSwitchProps = (props) => {
	const { innerModel, activeTabKey, readOnly = false } = props || {};

	const hasDefinitions = Boolean(innerModel?.[activeTabKey]?.definitions?.length);
	return {
		checked: innerModel?.[activeTabKey]?.totalSpecialUnits,
		disabled: readOnly || innerModel?.globalUnitCounts || !hasDefinitions,
	};
};

export default function SpecialUnitsModelCommissions(props) {
	const {
		emptyMessage = "",
		mainProductLabel = "",
		commissions = null,
		frontProducts = [],
		backProducts = [],
		makes = [],
		loading = false,
		loadingMakes = false,
		loadingProducts = false,
		readOnly = false,
		onActionUpdate = () => {},
	} = props;

	// Used to add errors to the pay plan context
	const { errors, addError, clearErrors, removeError } = useErrors();
	const tabErrors = useMemo(() => Object.keys(AREAS).map((key) => Boolean(errors.get(key))), [errors]);

	// States
	// eslint-disable-next-line no-unused-vars
	const [tabIndex, setTabIndex] = useState(0);
	const [innerModel, setInnerModel] = useState(null);

	// Inner constants
	const tabsLabels = Object.entries(AREAS).map(([_, value]) => value.label);
	const activeTabKey = getTabAreaKey(tabIndex);

	const handleUpdateModels = (area, data) => {
		const updatedModel = { ...innerModel };
		set(updatedModel, [area, "definitions"], data);

		if (!data?.length) {
			updatedModel[area]["totalSpecialUnits"] = false;
		}

		setInnerModel(updatedModel);
		onActionUpdate?.(updatedModel);
	};

	const handleUpdateSwitch = (area, key, value) => {
		const updatedModel = { ...innerModel };
		if (!area) {
			updatedModel[key] = value;
			if (value) {
				Object.keys(AREAS).forEach((indexKey) => {
					updatedModel[indexKey]["totalSpecialUnits"] = false;
				});
			}
		} else {
			updatedModel[area][key] = value;
		}
		setInnerModel(updatedModel);
		onActionUpdate?.(updatedModel);
	};

	const handleValidation = (key, isValid) => {
		!isValid ? addError(key) : removeError(key);
	};

	useEffect(() => {
		setInnerModel(mapInitialModel(commissions));

		return () => {
			setInnerModel(null);
			clearErrors();
		};
	}, [commissions]);

	const { checked, disabled } = getTotalSpecialSwitchProps({ innerModel, activeTabKey, readOnly });

	return (
		<SectionContainer sectionId={"rules"} title={"Rules"} sx={{ marginBottom: "-10px", boxShadow: "none !important" }}>
			<Grid container>
				<Grid item xs={12} mt={2.5}>
					<TabSelectable
						tabs={tabsLabels}
						errors={tabErrors}
						onTabChange={(_, information) => setTabIndex(information?.index)}
						content={Object.entries(AREAS)?.map(([key, value], index) => (
							<Grid container key={`${key}_${index}`}>
								<Grid item xs={12} mb={1} className={"models-tiered-area"}>
									<ModelBased
										key={`${key}_${value}`}
										modelKey={key}
										frontProductLabel={mainProductLabel}
										backProductLabel={mainProductLabel}
										frontProducts={frontProducts}
										backProducts={backProducts}
										makes={makes}
										models={innerModel?.[key]?.definitions}
										modelsAddons={{
											regularExpression: true,
											miniDeal: true,
											applyRetroactively: true,
										}}
										emptyError={{
											emptyMessage,
											error: false,
										}}
										loading={loading}
										loadingProducts={loadingProducts}
										loadingMakes={loadingMakes}
										readOnly={readOnly}
										addButtonText={"Add Rule"}
										onActionUpdate={(resultModel) => handleUpdateModels(key, resultModel)}
										onActionValidation={(isValid) => handleValidation(key, isValid)}
									/>
								</Grid>
							</Grid>
						))}
						topActions={[
							<CustomizedSwitches
								key={"globalUnitCounts"}
								textLeft={true}
								textChecked={"Unit count combined"}
								textUnChecked={"Unit count combined"}
								checked={innerModel?.globalUnitCounts}
								disabled={readOnly}
								tooltip={{
									title: "Uses all units of New and Pre-Owned Vehicles together.",
									placement: "bottom",
									key: "model-commissions-global-unit-counts-tooltip",
								}}
								onChange={(value) => handleUpdateSwitch(null, "globalUnitCounts", value)}
							/>,
						]}
						bottomActions={[
							<CustomizedSwitches
								key={"totalSpecialUnits"}
								textLeft={true}
								textChecked={"Total Special Units"}
								textUnChecked={"Total Special Units"}
								checked={checked}
								disabled={disabled}
								onChange={(value) => handleUpdateSwitch(activeTabKey, "totalSpecialUnits", value)}
							/>,
						]}
					/>
				</Grid>
			</Grid>
		</SectionContainer>
	);
}

SpecialUnitsModelCommissions.propTypes = {
	emptyMessage: PropTypes.string,
	mainProductLabel: PropTypes.string,
	commissions: PropTypes.object,
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	makes: PropTypes.array,
	loading: PropTypes.bool,
	loadingMakes: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
